<template>
  <div class="strategies-page">
    <div class="container">
      <div class="strategies-header animate-fade-in-down">
        <h1>{{ t('strategies.title') }}</h1>
        <p>{{ t('strategies.subtitle') }}</p>
      </div>

      <div class="strategies-grid">
        <a
          v-for="article in articles"
          :key="article.id"
          class="strategy-card animate-fade-in-up"
          :href="'#/strategies/' + article.id"
        >
          <div class="card-content">
            <div class="card-meta">
              <span class="category-tag">{{ t(`games.${article.category}.title`) }}</span>
              <span class="date">{{ formatDate(article.date) }}</span>
            </div>
            <h3>{{ article.title }}</h3>
            <p class="summary">{{ article.summary }}</p>
            <div class="card-footer">
              <span class="read-time">{{ article.readTime }}</span>
              <span class="read-more">{{ t('strategies.readMore') }} →</span>
            </div>
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from '@/utils/i18n'
import { strategies } from '@/data/strategies'

const { t, currentLang } = useI18n()

const articles = computed(() => {
  return strategies.map(article => {
    // Determine suffix based on lang: 'zh' -> '_zh', 'en' -> '_en', 'fr' -> '_fr'
    const suffix = currentLang.value === 'zh' ? '_zh' : (currentLang.value === 'fr' ? '_fr' : '_en')
    
    // Fallback to English if translation missing (though we should ensure all exist)
    return {
      ...article,
      title: article[`title${suffix}`] || article.title_en,
      summary: article[`summary${suffix}`] || article.summary_en,
      readTime: article[`readTime${suffix}`] || article.readTime_en,
      content: article[`content${suffix}`] || article.content_en
    }
  })
})

const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat(currentLang.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date)
}
</script>

<style scoped>
.strategies-page {
  padding: var(--space-4xl) 0;
  background-color: var(--color-bg-light);
  min-height: 80vh;
}

.strategies-header {
  text-align: center;
  margin-bottom: var(--space-4xl);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.strategies-header h1 {
  font-size: clamp(32px, 5vw, 48px);
  margin-bottom: var(--space-base);
  color: var(--color-text-primary);
}

.strategies-header p {
  font-size: var(--font-size-h4);
  color: var(--color-text-secondary);
}

.strategies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: var(--space-2xl);
}

.strategy-card {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out);
  display: flex;
  flex-direction: column;
  text-decoration: none; /* Remove underline from anchor */
}

.strategy-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: var(--color-primary-orange);
}

.card-content {
  padding: var(--space-xl);
  display: flex;
  flex-direction: column;
  flex: 1;
}

.card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-md);
  font-size: var(--font-size-small);
}

.category-tag {
  background: rgba(242, 78, 30, 0.1); 
  color: var(--color-primary-orange);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  font-size: 12px;
}

.date {
  color: var(--color-text-tertiary);
}

.strategy-card h3 {
  font-size: var(--font-size-h4);
  margin-bottom: var(--space-md);
  color: var(--color-text-primary);
  line-height: 1.4;
}

.summary {
  color: var(--color-text-secondary);
  font-size: var(--font-size-body);
  margin-bottom: var(--space-xl);
  line-height: 1.6;
  flex: 1;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: var(--space-md);
  border-top: 1px solid var(--color-border-light);
  color: var(--color-text-tertiary);
  font-size: var(--font-size-small);
}

.read-more {
  color: var(--color-primary-orange);
  font-weight: var(--font-weight-semibold);
}

@media (max-width: 640px) {
  .strategies-grid {
    grid-template-columns: 1fr;
  }
}
</style>
